import * as THREE from 'three';
import Time from '../Utils/Time';
import Application from '../Application';
import { SCREEN_SIZE, SCREEN_POSITION, SCREEN_TILT_X, CAMERA_FOV, BEZEL_T, } from '../World/ScreenConfig';
export class CameraKeyframeInstance {
    constructor(keyframe) {
        this.position = keyframe.position;
        this.focalPoint = keyframe.focalPoint;
    }
    update() { }
}
/**
 * Rapproche un cadrage de son point vise, sans changer l'angle de vue.
 *
 * On interpole la position vers le point focal plutot que de diviser les
 * coordonnees : le point vise n'est pas l'origine, une simple division
 * deplacerait aussi la direction du regard.
 *
 * ZOOM = 0.5 divise par deux la distance camera/sujet, soit le "zoom 50%"
 * demande sur les plans larges.
 */
const ZOOM = 0.5;
const closer = (position, focalPoint, factor = ZOOM) => focalPoint.clone().lerp(position, factor);
const keys = {
    idle: (() => {
        const focalPoint = new THREE.Vector3(0, -1000, 0);
        return {
            position: closer(new THREE.Vector3(-20000, 12000, 20000), focalPoint),
            focalPoint,
        };
    })(),
    monitor: {
        position: new THREE.Vector3(0, SCREEN_POSITION.y, 2000),
        focalPoint: new THREE.Vector3(0, SCREEN_POSITION.y, 0),
    },
    desk: {
        position: new THREE.Vector3(0, 1800, 5500),
        focalPoint: new THREE.Vector3(0, 500, 0),
    },
    loading: (() => {
        const focalPoint = new THREE.Vector3(0, -5000, 0);
        return {
            position: closer(new THREE.Vector3(-35000, 35000, 35000), focalPoint),
            focalPoint,
        };
    })(),
    orbitControlsStart: (() => {
        const focalPoint = new THREE.Vector3(-100, 350, 0);
        return {
            position: closer(new THREE.Vector3(-15000, 10000, 15000), focalPoint),
            focalPoint,
        };
    })(),
};
export class MonitorKeyframe extends CameraKeyframeInstance {
    constructor() {
        const keyframe = keys.monitor;
        super(keyframe);
        this.application = new Application();
        this.sizes = this.application.sizes;
        this.origin = new THREE.Vector3().copy(keyframe.position);
        this.targetPos = new THREE.Vector3().copy(keyframe.position);
    }
    update() {
        // Contain-fit the 16:9 screen in the viewport: pick whichever distance
        // (fit-by-height vs fit-by-width) keeps the WHOLE screen in frame, so
        // there's no leftover empty band on either axis like the old 5:4 zoom had.
        const tanHalfFov = Math.tan((CAMERA_FOV * THREE.MathUtils.DEG2RAD) / 2);
        const viewAspect = this.sizes.width / this.sizes.height;
        // Frame the screen PLUS one bezel thickness on every side, so the TV
        // frame is visible with the same thickness on all 4 edges.
        const framedH = SCREEN_SIZE.h + BEZEL_T * 2;
        const framedW = SCREEN_SIZE.w + BEZEL_T * 2;
        const distForHeight = framedH / (2 * tanHalfFov);
        const distForWidth = framedW / (2 * tanHalfFov * viewAspect);
        const distance = Math.max(distForHeight, distForWidth);
        this.targetPos.z = SCREEN_POSITION.z + distance;
        // The screen is tilted back by SCREEN_TILT_X, so its top edge is a bit
        // farther from the camera than its bottom edge and looks smaller. Aim
        // the camera slightly off-centre so the top and bottom margins match
        // (equal projected height above and below the view axis).
        const halfH = SCREEN_SIZE.h / 2;
        const sinT = Math.sin(SCREEN_TILT_X);
        const cosT = Math.cos(SCREEN_TILT_X);
        const topY = SCREEN_POSITION.y + halfH * cosT;
        const bottomY = SCREEN_POSITION.y - halfH * cosT;
        const topDepth = distance - halfH * sinT;
        const bottomDepth = distance + halfH * sinT;
        const viewY = (topY * bottomDepth + bottomY * topDepth) / (topDepth + bottomDepth);
        this.targetPos.y = viewY;
        this.focalPoint.y = viewY;
        this.position.copy(this.targetPos);
    }
}
export class LoadingKeyframe extends CameraKeyframeInstance {
    constructor() {
        const keyframe = keys.loading;
        super(keyframe);
    }
    update() { }
}
export class DeskKeyframe extends CameraKeyframeInstance {
    constructor() {
        const keyframe = keys.desk;
        super(keyframe);
        this.application = new Application();
        this.mouse = this.application.mouse;
        this.sizes = this.application.sizes;
        this.origin = new THREE.Vector3().copy(keyframe.position);
        this.targetFoc = new THREE.Vector3().copy(keyframe.focalPoint);
        this.targetPos = new THREE.Vector3().copy(keyframe.position);
    }
    update() {
        this.targetFoc.x +=
            (this.mouse.x - this.sizes.width / 2 - this.targetFoc.x) * 0.05;
        this.targetFoc.y +=
            (-(this.mouse.y - this.sizes.height) - this.targetFoc.y) * 0.05;
        this.targetPos.x +=
            (this.mouse.x - this.sizes.width / 2 - this.targetPos.x) * 0.025;
        this.targetPos.y +=
            (-(this.mouse.y - this.sizes.height * 2) - this.targetPos.y) *
                0.025;
        const aspect = this.sizes.height / this.sizes.width;
        this.targetPos.z = this.origin.z + aspect * 3000 - 1800;
        this.focalPoint.copy(this.targetFoc);
        this.position.copy(this.targetPos);
    }
}
export class IdleKeyframe extends CameraKeyframeInstance {
    constructor() {
        const keyframe = keys.idle;
        super(keyframe);
        this.origin = new THREE.Vector3().copy(keyframe.position);
        this.time = new Time();
    }
    update() {
        this.position.x =
            Math.sin((this.time.elapsed + 19000) * 0.00008) * this.origin.x;
        this.position.y =
            Math.sin((this.time.elapsed + 1000) * 0.000004) * 4000 +
                this.origin.y -
                3000;
        this.position.z = this.position.z;
    }
}
export class OrbitControlsStart extends CameraKeyframeInstance {
    constructor() {
        const keyframe = keys.orbitControlsStart;
        super(keyframe);
    }
    update() { }
}
